"use client"

import React, {useEffect, useState} from 'react';
import styles from "./SinglePlace.module.scss"
import {useLocale, useTranslations} from "use-intl";
import PlaceCategory from "@/components/PlaceCategory/PlaceCategory";
import PlaceType from "@/components/PlaceType/PlaceType";
import Cover from '@/components/Cover/Cover';
import {useGetFeaturedImage} from "@/utils/hooks/useGetFeaturedImage";
import usePlaceData from "@/utils/hooks/usePlaceData";
import {LuMapPin} from "react-icons/lu";
import {BsTelephone} from "react-icons/bs";
import {FaExternalLinkAlt, FaInfoCircle} from "react-icons/fa";
import {MdOutlineMail} from "react-icons/md";
import Preload from "@/components/SinglePlace/Preload";
import {segmentRequest} from "@/utils/api/Segment";
import {Segment} from "@/types/Segments";
import {Link} from "@/i18n/routing";
import {useSinglePlaceIcon} from "@/utils/hooks/usePlaceIcon";
import SinglePlaceMap from "@/components/SinglePlace/SinglePlaceMap";

export default function SinglePlace(props: { id: string }) {
  const locale = useLocale()
  const t = useTranslations("Place")
  const data = usePlaceData(props.id)
  const featured = useGetFeaturedImage(data?.images)
  const [segment, setSegment] = useState<Segment | undefined>(undefined);
  const icon = useSinglePlaceIcon(data)

  useEffect(() => {
    const fetchData = async () => {
      const fetched = await segmentRequest(String(data?.segment_id), locale)
      if (fetched) {
        setSegment(fetched);
      }
    }
    if (data?.segment_id) {
      fetchData();
    }
  }, [data, locale]);


  if (!data) return <Preload/>
  const {
    name,
    content,
    categories,
    types,
    address,
    website,
    phone,
    segment_id,
    source_name,
    source_url,
    email,
  } = data

  return <div className={styles.container}>
    <Cover backElements={<h4>{segment?.name}</h4>} image={featured.url}
           link={segment_id ? `/tappa/${segment_id}` : '/places'}
           title={name}>
      <div className={styles.typeCatWrapper}>
        <PlaceType className={styles.type} id={String(types[0].id)}/>
        <PlaceCategory className={styles.category} id={String(categories[0].id)}/>
        {source_url && source_url === "" && <small className={styles.source}>{source_name}</small>}
        {source_url && source_url !== "" &&
            <Link target="_blank" href={source_url}><small className={styles.source}>{source_name}</small></Link>}
        {icon}
      </div>
    </Cover>
    <SinglePlaceMap data={data}/>
    <div className={styles.info}>
      {address && address !== "" && <p className={styles.infoWrapper}>
        <div className={styles.innerWrapper}>
          <LuMapPin/><strong>{t('address')} :</strong>
        </div>
        {address}
      </p>}
      {phone && phone !== "" && <p className={styles.infoWrapper}>
        <div className={styles.innerWrapper}>
          <BsTelephone/>
          <strong>{t('phone')} :</strong>
        </div>
        {phone}
      </p>}
      {website && website !== "" && <p className={styles.infoWrapper}>
        <div className={styles.innerWrapper}>
          <FaExternalLinkAlt/>
          <strong>{t('website')} :</strong>
        </div>
        <Link style={{textDecoration: "underline"}} href={website} target="_blank">{website}</Link>
      </p>
      }
      {email && email !== "" && <p className={styles.infoWrapper}>
        <div className={styles.innerWrapper}>
          <MdOutlineMail/>
          <strong>{t('email')} :</strong>
        </div>
        {email}
      </p>}
      {source_url && source_name && source_url !== "" && <p className={styles.infoWrapper}>
        <div className={styles.innerWrapper}>
          <FaInfoCircle/>
          <strong>{t('info')} :</strong>
        </div>
        <Link style={{textDecoration: "underline"}} href={source_url} target="_blank">
          {source_name}
        </Link>
      </p>
      }
    </div>
    <div className={styles.content} dangerouslySetInnerHTML={{__html: content}}/>
  </div>
}
